import { useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { BsBarChart, BsLink45Deg, BsPeople, BsPercent } from 'react-icons/bs';
import { LINK_CLICKS_TAB } from '../MePageTabs';
import ClicksByCountry from './ClicksByCountry';
import ClicksByDaytime from './ClicksByDaytime';
import ClicksByDevice from './ClicksByDevice';
import ClicksOverTime from './ClicksOverTime';
import LinkClicksEmptyState, {
  LINK_CLICKS_EMPTY_STATES
} from './LinkClicksEmptyState';
import LinkClicksStatCard from './LinkClicksStatCard';
import LinkPerformanceTable from './LinkPerformanceTable';
import useLinkClicksRequest from '../../../../hooks/useLinkClicksRequest';
import { useTranslation } from 'react-i18next';

const EMPTY_VALUE = '—';
const PAGE_SPECIFIC_EMPTY_STATES = [
  LINK_CLICKS_EMPTY_STATES.NO_CLICKS,
  LINK_CLICKS_EMPTY_STATES.NO_LINKS
];

const resolveEmptyState = ({
  links,
  isFiltersActive,
  selectedPageId,
  emptyState,
  hasSeenAnalytics
}) => {
  if (!links.length) {
    if (!selectedPageId) return null;

    if (emptyState === LINK_CLICKS_EMPTY_STATES.NO_LINKS) {
      return LINK_CLICKS_EMPTY_STATES.NO_LINKS;
    }

    if (hasSeenAnalytics && isFiltersActive) {
      return LINK_CLICKS_EMPTY_STATES.NO_DATA;
    }

    return LINK_CLICKS_EMPTY_STATES.NO_CLICKS;
  }

  if (emptyState === LINK_CLICKS_EMPTY_STATES.NO_LINKS) return null;
  return !selectedPageId && PAGE_SPECIFIC_EMPTY_STATES.includes(emptyState)
    ? null
    : emptyState;
};

const LinkClicksTab = ({
  isFiltersActive = false,
  selectedPageId,
  emptyState,
  hasSeenAnalytics = false,
  onEmptyStateAction,
  onResetFilters,
  emptyStateActionHref,
  onDataChange
}) => {
  const { filters, page } = useSelector((state) => ({
    filters: state.filters,
    page: state.meQrData.pagination.page
  }));
  const { t, i18n } = useTranslation();
  const [linkClicks, setLinkClicks] = useState(null);

  const { getLinkClicks } = useLinkClicksRequest();
  useEffect(() => {
    onDataChange?.(null);
    getLinkClicks().then((data) => {
      setLinkClicks(data);
      onDataChange?.(data);
    });
    // eslint-disable-next-line
  }, [filters, page]);

  if (!linkClicks) {
    return null;
  }

  const { summary, trend, daytime, byCountry, byDevice, links } = linkClicks;
  const topLink = links[0] ?? null;
  const resolvedEmptyState = resolveEmptyState({
    links,
    isFiltersActive,
    selectedPageId,
    emptyState,
    hasSeenAnalytics
  });

  const pageId = encodeURIComponent(selectedPageId);
  const emptyStateActionHrefs = {
    [LINK_CLICKS_EMPTY_STATES.NO_LINKS]: `https://constructor.me-page.com/${pageId}`,
    [LINK_CLICKS_EMPTY_STATES.NO_CLICKS]: `https://admin.me-page.com/user/access/page/${pageId}/access`
  };
  const resolvedActionHref = selectedPageId
    ? (emptyStateActionHrefs[resolvedEmptyState] ?? emptyStateActionHref)
    : emptyStateActionHref;

  if (resolvedEmptyState) {
    return (
      <div
        id={`${LINK_CLICKS_TAB}-panel`}
        role="tabpanel"
        aria-labelledby={`${LINK_CLICKS_TAB}-tab`}
        className="me-page-link-clicks"
      >
        <LinkClicksEmptyState
          type={resolvedEmptyState}
          onAction={
            resolvedEmptyState === LINK_CLICKS_EMPTY_STATES.NO_DATA
              ? onResetFilters
              : onEmptyStateAction
          }
          actionHref={resolvedActionHref}
        />
      </div>
    );
  }

  return (
    <div
      id={`${LINK_CLICKS_TAB}-panel`}
      role="tabpanel"
      aria-labelledby={`${LINK_CLICKS_TAB}-tab`}
      className="me-page-link-clicks d-flex flex-column gap-3 gap-md-4 mt-0 px-0"
    >
      <Row className="g-3 g-md-4">
        <Col xs={12} md={6}>
          <LinkClicksStatCard
            title={t('linkClicks.totalLinkClicks')}
            description={t('linkClicks.totalLinkClicksDescription')}
            value={summary.totalClicks.toLocaleString(i18n.language)}
            icon={BsBarChart}
          />
        </Col>
        <Col xs={12} md={6}>
          <LinkClicksStatCard
            title={t('linkClicks.uniqueLinkClicks')}
            description={t('linkClicks.uniqueLinkClicksDescription')}
            value={summary.uniqueVisitors.toLocaleString(i18n.language)}
            icon={BsPeople}
          />
        </Col>
        <Col xs={12} md={6}>
          <LinkClicksStatCard
            title={t('linkClicks.topPerformingLink')}
            description={t('linkClicks.topPerformingLinkDescription')}
            value={
              topLink ? (
                <>
                  <span className="fs-5">{topLink.label}</span>{' '}
                  <span className="text-gray fs-6 fw-normal">
                    / {topLink.totalClicks.toLocaleString(i18n.language)}{' '}
                    {t('linkClicks.clicks').toLowerCase()}
                  </span>
                </>
              ) : (
                EMPTY_VALUE
              )
            }
            icon={BsLink45Deg}
          />
        </Col>
        <Col xs={12} md={6}>
          <LinkClicksStatCard
            title={t('linkClicks.linkCtr')}
            description={t('linkClicks.linkCtrDescription')}
            value={
              summary.ctr === null ? EMPTY_VALUE : `${summary.ctr.toFixed(1)}%`
            }
            icon={BsPercent}
          />
        </Col>
      </Row>

      <LinkPerformanceTable links={links} />

      <ClicksOverTime trend={trend} />

      <Row className="g-3 g-md-4">
        <Col xs={12} lg={5} xxl={4}>
          <ClicksByDevice devices={byDevice} />
        </Col>
        <Col xs={12} lg={7} xxl={8}>
          <ClicksByCountry
            countries={byCountry.items}
            otherClicks={byCountry.otherClicks}
            totalClicks={summary.totalClicks}
          />
        </Col>
      </Row>

      <ClicksByDaytime daytime={daytime} />
    </div>
  );
};

export default LinkClicksTab;
